import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Pipeline.module.scss';
import { useGetKpiSettingsQuery } from '../../../../redux/services/kpiSettingsByTypeAPI';
import { useProfileParams, isAISummariesAccessible } from '../../../../utils/common';
import InsightAI from '../../../common/InsightAI/InsightAI';
import ABOApplicationsTile, {
  ABOApplicationsDetail,
} from './KPI/ABOApplications/ABOApplicationsTile';
import ProductivityPerContributorTile, {
  ProductivityPerContributorDetail,
} from './KPI/ProductivityPerContributor/ProductivityPerContributorTile';
import ProductivityPerBuyerTile, {
  ProductivityPerBuyerDetail,
} from './KPI/ProductivityPerBuyer/ProductivityPerBuyerTile';
import { useGetBusinessPerformanceSummaryQuery } from '../../../../redux/services/businessPerformaceAPI';
import isEmpty from 'lodash/isEmpty';
import ActivationRateTile, { ActivationRateDetail } from './KPI/ActivationRate/ActivationRateTile';
import AverageBronzeTile, { AverageBronzeDetail } from './KPI/AverageBronze/AverageBronzeTile';
import InteractiveTileGrid from '../common/InteractiveTileGrid/InteractiveTileGrid';
import ErrorInPageScreen from '../../../common/ErrorInPageScreen/ErrorInPageScreen';
import { KpiSettingsItem } from '../../../../types/types';

const Pipeline: React.FC<CategoriesProps> = () => {
  const { t } = useTranslation();
  const [pipelineKPIsCodelist, setPipelineKPIsCodelist] = useState<any>([]);
  const { affiliateCode, aboNumber } = useProfileParams();

  const {
    data: pipelineKpisResponse,
    isFetching: isFetchingKpiSettings,
    isError: isKpiSettingsError,
    refetch: refetchKpiSettings,
  } = useGetKpiSettingsQuery(
    {
      type: 'business-performance-pipeline',
      affiliateCode,
    },
    { refetchOnMountOrArgChange: true }
  );

  const {
    data: performanceSummaryResponse,
    isLoading: isLoadingSummary,
    isError: isSummaryError,
    refetch: refetchSummary,
  } = useGetBusinessPerformanceSummaryQuery(
    { affiliateCode, aboNumber, payload: { kpis: pipelineKPIsCodelist } },
    {
      refetchOnMountOrArgChange: true,
      skip: Number(affiliateCode) === 0 || aboNumber === 0 || isEmpty(pipelineKPIsCodelist),
    }
  );

  const loading = isLoadingSummary || isFetchingKpiSettings;
  const error = isKpiSettingsError || isSummaryError;

  // Safe retry handler - only refetch queries that have been started
  const handleRetry = () => {
    refetchKpiSettings();
    // Only refetch summary if the query was started (not skipped)
    if (!isEmpty(pipelineKPIsCodelist)) {
      refetchSummary();
    }
  };

  useEffect(() => {
    const response = pipelineKpisResponse?.body || [];

    const kpiCodes = response.map((item: KpiSettingsItem) => {
      return {
        code: item?.code === 'activationRate:90|30|60' ? 'activationRate90' : item?.code,
      };
    });

    setPipelineKPIsCodelist(kpiCodes);
  }, [pipelineKpisResponse]);

  // Extract individual KPI data from summary response
  const kpisData = performanceSummaryResponse?.body?.kpis || [];
  const aboAppsSummary = kpisData.find((kpi: any) => kpi.kpiCode === 'aboApplications');
  const activationRateSummary = kpisData.find((kpi: any) => kpi.kpiCode === 'activationRate90');
  const productivityPerContributorSummary = kpisData.find((kpi: any) => kpi.kpiCode === 'productivityPerContributor');
  const productivityPerBuyerSummary = kpisData.find((kpi: any) => kpi.kpiCode === 'productivityPerBuyer');
  const averageBronzeSummary = kpisData.find((kpi: any) => kpi.kpiCode === 'averageBronze');

  return (
    <div className={styles.pipeline_wrapper}>
      <div className={styles.heading_area}>
        <div className={styles.heading}>{t('pipeline')}</div>
      </div>
      {isAISummariesAccessible() && (
        <InsightAI
          content={'AiContent is here'}
          className={styles.insight_ai}
          showDropdown={true}
          updateDate="08/19/2024"
          isDropdownEnabled={true}
          flowerIconSize={24}
          title={'ai_summary_pipeline'}
        />
      )}
      <InteractiveTileGrid
        className={styles.tiles_grid_container}
        tiles={[
          {
            id: 'aboApplications',
            kpiTile: error ? (
              <ErrorInPageScreen handleClickTryAgain={handleRetry} />
            ) : (
              <ABOApplicationsTile
                summary={aboAppsSummary}
                loading={loading}
                affiliateCode={affiliateCode}
              />
            ),
            kpiDetailTile: <ABOApplicationsDetail kpiCode="aboApplications" />,
          },
          {
            id: 'activationRate',
            kpiTile: error ? (
              <ErrorInPageScreen handleClickTryAgain={handleRetry} />
            ) : (
              <ActivationRateTile
                summary={activationRateSummary}
                loading={loading}
                affiliateCode={affiliateCode}
              />
            ),
            kpiDetailTile: <ActivationRateDetail kpiCode="activationRate90" />,
          },
          {
            id: 'productivityPerContributor',
            kpiTile: error ? (
              <ErrorInPageScreen handleClickTryAgain={handleRetry} />
            ) : (
              <ProductivityPerContributorTile
                summary={productivityPerContributorSummary}
                loading={loading}
                affiliateCode={affiliateCode}
              />
            ),
            kpiDetailTile: <ProductivityPerContributorDetail />,
          },
          {
            id: 'productivityPerBuyer',
            kpiTile: error ? (
              <ErrorInPageScreen handleClickTryAgain={handleRetry} />
            ) : (
              <ProductivityPerBuyerTile
                summary={productivityPerBuyerSummary}
                loading={loading}
                affiliateCode={affiliateCode}
              />
            ),
            kpiDetailTile: <ProductivityPerBuyerDetail />,
          },
          {
            id: 'averageBronze',
            kpiTile: error ? (
              <ErrorInPageScreen handleClickTryAgain={handleRetry} />
            ) : (
              <AverageBronzeTile summary={averageBronzeSummary} loading={loading} />
            ),
            kpiDetailTile: <AverageBronzeDetail kpiCode="averageBronze" />,
          },
        ]}
      />
    </div>
  );
};

export default Pipeline;
